import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, FileDown } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, EmptyState, Tag, Btn } from "@/components/kit";
import { VehicleHistory } from "@/components/VehicleHistory";
import { PhotoGallery } from "@/components/PhotoGallery";
import { useAsync } from "@/lib/hooks";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { formatReg, fmtDate } from "@/lib/vda";

async function loadStaffVehicle(id) {
  const { data: v } = await supabase.from("vehicles").select("*").eq("id", id).maybeSingle();
  if (!v) return { vehicle: null };
  const [{ data: cust }, { data: events }, att] = await Promise.all([
    supabase.from("customers").select("id, name, lifecycle_status").eq("id", v.customer_id).maybeSingle(),
    supabase.from("vehicle_history_events").select("*").eq("vehicle_id", id).order("event_at", { ascending: false }).limit(500),
    supabase.from("booking_attachments")
      .select("id,storage_path,file_name,file_type,visibility,booking_requests!inner(vehicle_id)")
      .eq("booking_requests.vehicle_id", id),
  ]);
  return { vehicle: v, customer: cust, events: events || [], attachments: att?.data || [] };
}

// #3/#8/#26 Comprehensive, auditable vehicle record for internal staff — the
// working history intended to replace F12 over time.
export default function StaffVehicle() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, loading } = useAsync(() => loadStaffVehicle(id), [id]);
  const [exporting, setExporting] = React.useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const blob = await api.vehicleAuditPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relay-audit-${(data?.vehicle?.registration || "vehicle").replace(/\s/g, "")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Could not export the audit PDF."); }
    finally { setExporting(false); }
  };

  if (loading) return <StaffShell><Spinner /></StaffShell>;
  const v = data.vehicle;
  if (!v) return <StaffShell><EmptyState title="Vehicle not found" /></StaffShell>;

  return (
    <StaffShell>
      <button data-testid="sv-back" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Back
      </button>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Vehicle record</p>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="vda-reg text-3xl font-semibold tracking-wide text-primary">{formatReg(v.registration)}</h1>
          <p className="text-foreground/80">{v.make} {v.model} {v.derivative || ""} · {data.customer?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {data.customer?.lifecycle_status && data.customer.lifecycle_status !== "active" && (
            <Tag tone="amber">{data.customer.lifecycle_status.replace(/_/g, " ")}</Tag>
          )}
          <span className="text-xs text-muted-foreground">MOT {fmtDate(v.mot_due_date)}</span>
          <Btn variant="outline" data-testid="sv-export-pdf" onClick={exportPdf} loading={exporting}>
            <FileDown className="h-4 w-4 mr-1.5" /> Export audit PDF
          </Btn>
        </div>
      </div>

      <Panel className="p-5">
        <h2 className="font-semibold mb-4">Operational history</h2>
        <VehicleHistory events={data.events} testId="sv-history" />
      </Panel>

      <Panel className="p-5 mt-5">
        <h2 className="font-semibold mb-1">Evidence photos</h2>
        <p className="text-xs text-muted-foreground">Photos attached to this vehicle's requests (accident reports and other evidence).</p>
        <PhotoGallery attachments={data.attachments} testId="sv-gallery" emptyText="No evidence photos attached yet." />
      </Panel>
    </StaffShell>
  );
}
